import { ColorContext } from 'App';
import { FusionInputField } from 'components/index';
import { usePerms } from 'contexts/PermsContext';
import React, { useContext, useEffect, useState } from 'react';
import { Button, Card, InputGroup, OverlayTrigger, Popover } from 'react-bootstrap';
import { FaInfoCircle, FaInfoCircle } from 'react-icons/fa';
import { PiWarningFill } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import {
  getGlobalActions,
  insertGlobalAction,
  updateGlobalAction,
} from 'services/globalActions/globalActions.service';
import { getCityStateFromCoordinates } from 'services/location/location.service';
import { ArrayToLinkedList, LinkedListToArray } from 'utils/parserUtils';
import GlobalActionsDropdown from './GlobalActionsDropdown';

export const GlobalActionsBuilder = ({
  setShowNewGlobalAction,
  setGlobalActions,
  initialData, // for editing
  setInitialData,
}) => {
  const navigate = useNavigate();
  const perms = usePerms();
  const addGlobalAction = (action) => {
    setInitialData(null);
    setSubmitting(true);

    if (initialData) {
      const updatedData = {
        ...action,
        title: actionTitle,
        debounceTime: debounceTime || 0,
        minEventTime: minEventTime || 0,
      };
      updateGlobalAction(initialData.id, updatedData).then((res) => {
        // Then fetch new global actions from server
        if (res.status === 200) {
          getGlobalActions().then((res) => {
            setGlobalActions(res.data.result);
            setShowNewGlobalAction(false);
            setSubmitting(false);
          });
        } else {
          setError({ message: 'Failed to update global action.' });
          setSubmitting(false);
        }
      });
    } else {
      console.log('Inserting global action:', action);
      insertGlobalAction(action).then((res) => {
        // Then fetch new global actions from server
        if (res.status === 200) {
          getGlobalActions().then((res) => {
            setGlobalActions(res.data.result);
            setShowNewGlobalAction(false);
            setSubmitting(false);
          });
        } else {
          setError({ message: 'Failed to update global action.' });
          setSubmitting(false);
        }
      });
    }
  };

  const [actionTitle, setActionTitle] = useState('');
  const [debounceTime, setDebounceTime] = useState(0);
  const [minEventTime, setMinEventTime] = useState(0);

  const [retrigger, setRetrigger] = useState('');

  const [anyErrors, setAnyErrors] = useState(true);
  const [error, setError] = useState(null);

  const theme = useContext(ColorContext);

  const [head, setHead] = useState({
    type: 'if',
    selectedAction: '-Select-',
    integrationType: '-Select-',
    integrationData: {
      error: true,
    },
    showDropdown: false,
    next: null,
    prev: null,
  });

  const [list, setList] = useState([head]);

  const [submitting, setSubmitting] = useState(false);

  const menuItems = {
    if: ['-Select-', 'Device', 'Device Group', 'Date & Time', 'Date', 'Time'],
    then: ['-Select-', 'Device Action', 'Device Group Action', 'Send Email', 'Send SMS'],
    until: [
      '-Select-',
      'Original Condition Resolved',
      'Device',
      'Device Group',
      'Date & Time',
      'Date',
      'Time',
    ],
    done: ['-Select-', 'submit'],
  };

  const latLongToCityState = async (lat, long) => {
    let city, state;
    try {
      const result = await getCityStateFromCoordinates(lat, long);
      city = result.data.result.city_name;
      state = result.data.result.state_name;
      if (city && state) {
        return { city: city, state: state };
      }
    } catch (error) {
      console.error('Error getting city and state:', error);
    }
  };

  const renderList = (listHead) => {
    setList(LinkedListToArray(listHead));
  };

  useEffect(() => {
    renderList(head);
  }, [retrigger]);

  useEffect(() => {
    (async () => {
      if (!(await perms.validatePermissions(['ViewConnections', 'Create-EditGlobalActions']))) {
        navigate('/404');
        return;
      }
    })();
  }, []);

  useEffect(() => {
    if (initialData) {
      //RECONSTRUCT DATA INTO GLOBAL ACTION
      setActionTitle(initialData.title);
      setDebounceTime(initialData.debounceTime || 0);
      setMinEventTime(initialData.minEventTime || 0);

      try {
        const rawDataList = JSON.parse(initialData.rawDataList);
        //build linked list
        const listHead = ArrayToLinkedList(rawDataList);
        setList(rawDataList);
        setHead(listHead);
        renderList(listHead);
      } catch (e) {}
    } else {
    }
  }, []);

  const getAnyErrors = () => {
    //check if any until date/time is before if date/time
    const ifNodes = list.filter((item) => item.type === 'if');
    const untilNodes = list.filter((item) => item.type === 'until');

    const ifDates = ifNodes.map((item) => {
      if (item.integrationType === 'Date & Time') {
        return item.integrationData.dateTime;
      } else if (item.integrationType === 'Date') {
        return item.integrationData.date;
      } else if (item.integrationType === 'Time') {
        return item.integrationData.time;
      }
    });

    const untilDates = untilNodes.map((item) => {
      if (item.integrationType === 'Date & Time') {
        return item.integrationData.dateTime;
      } else if (item.integrationType === 'Date') {
        return item.integrationData.date;
      } else if (item.integrationType === 'Time') {
        return item.integrationData.time;
      }
    });

    for (let i = 0; i < ifDates.length; i++) {
      for (let j = 0; j < untilDates.length; j++) {
        if (ifDates[i] >= untilDates[j]) {
          setError({ message: 'Invalid date/time.' });
          setAnyErrors(true);
          return true;
        }
      }
    }
    setError(null);
    setAnyErrors(false);

    if (
      list
        .map((item) => {
          if (item.type === 'done') return true;
          if (item.integrationType === 'Original Condition Resolved') return true;
          if (item.integrationData && item.integrationData.error) {
            setError({ message: 'Action not valid, check for errors.' });
            setAnyErrors(true);

            return false;
          } else return true;
        })
        .includes(false)
    ) {
      if (!error) setError({ message: 'Action not valid, check for errors.' });
      return true;
    } else {
      setError(null);
      setAnyErrors(false);
      return false;
    }
  };

  useEffect(() => {
    getAnyErrors();
  }, [list]);

  async function submitHandler() {
    // if (submitting) {
    //   return;
    // }

    //verify list
    if (getAnyErrors()) {
      setError({ message: 'Some action has an error.' });
      return false;
    }

    //apply function specific finishes to data
    for (let i = 0; i < list.length; i++) {
      switch (list[i].integrationData.selectedFunction) {
        // check for create radial alert and convert latlong to citystate
        case 'Create Radial Alert':
          const { city, state } = await latLongToCityState(
            list[i].integrationData.selectedDevice.location.lat,
            list[i].integrationData.selectedDevice.location.lng,
          );

          list[i].integrationData.city = city;
          list[i].integrationData.state = state;
          break;
        default:
          break;
      }
    }

    setSubmitting(true);

    const builtGlobalAction = BuildGlobalAction(list);
    const dateTriggerPayloads = builtGlobalAction.triggers
      .flat()
      .filter((trigger) => trigger?.type === 'date' || trigger?.type === 'datetime')
      .map((trigger) => ({
        type: trigger.type,
        value: trigger?.value?.value,
        iso: trigger?.value?.value ? new Date(trigger.value.value).toISOString() : null,
      }));
    if (dateTriggerPayloads.length > 0) {
      console.log('GlobalActions date payloads:', dateTriggerPayloads);
    }
    // console.log("Built global action:", builtGlobalAction); //Use this to verify before submitting
    addGlobalAction(builtGlobalAction);
  }

  //TODO: Break up this giant return block into smaller components
  return (
    <div className="page-scroll-area mt-2 p-2">
      {/* header card */}
      <Card className="flex-shrink-0" style={{ backgroundColor: theme.primaryShadow }}>
        <Card.Body>
          <div className="d-flex flex-column gap-3">
            {/* Title + input */}
            <div className="d-flex align-items-center gap-2">
              <span className="fs-5 fw-bold">{initialData ? 'Edit' : 'Add'} Global Action</span>
            </div>
            <div>
              <div className="d-flex align-items-center w-100 max-w-xl gap-3">
                <div className="flex-grow-1">
                  <FusionInputField
                    type="text"
                    name="actionTitle"
                    placeholder="Title"
                    onChange={setActionTitle}
                    value={actionTitle}
                    inputClassName="py-2"
                  />
                </div>
                {actionTitle.length === 0 && (
                  <PiWarningFill className="fs-5" style={{ color: 'yellow' }} />
                )}
              </div>
              {/* tiny helper under title when empty */}
              {actionTitle.length === 0 && (
                <div className="small text-warning mt-2">A title is required.</div>
              )}
            </div>

            {/* Debounce Time */}
            <div className="d-flex flex-column flex-md-row flex-wrap align-items-start align-items-md-center gap-3">
              <div className="d-flex align-items-center gap-2">
                <span className="fw-semibold d-inline-flex align-items-center gap-1">
                  Debounce Time
                  <OverlayTrigger
                    placement="bottom"
                    overlay={
                      <Popover>
                        <Popover.Header className="text-dark">
                          Debounce Time (0-1800s)
                        </Popover.Header>
                        <Popover.Body className="small">
                          Time the condition must stay true before triggering. Example: 30s means it
                          must remain true for the full 30 seconds.
                        </Popover.Body>
                      </Popover>
                    }
                  >
                    <span role="button" className="text-muted d-inline-flex">
                      <FaInfoCircle />
                    </span>
                  </OverlayTrigger>
                </span>

                <InputGroup>
                  <FusionInputField
                    type="number"
                    name="debounceTime"
                    onChange={setDebounceTime}
                    onBlur={() => setDebounceTime(Math.max(0, Math.min(1800, debounceTime)))}
                    value={debounceTime}
                    className="form-control border-0 bg-primary"
                    range={[0, 1800]}
                  />
                  <InputGroup.Text className="text-dark">secs</InputGroup.Text>
                </InputGroup>
              </div>

              {/* Min. Event Time */}
              <div className="d-flex align-items-center gap-2">
                <span className="fw-semibold d-inline-flex align-items-center gap-1">
                  Min. Event Time
                  <OverlayTrigger
                    placement="bottom"
                    overlay={
                      <Popover>
                        <Popover.Header className="text-dark">
                          Min. Event Time (0-7200s)
                        </Popover.Header>
                        <Popover.Body className="small">
                          Minimum time the action stays active once triggered. Prevents rapid on/off
                          flapping. Example: 300s keeps it on for 5 minutes.
                        </Popover.Body>
                      </Popover>
                    }
                  >
                    <span role="button" className="text-muted d-inline-flex">
                      <FaInfoCircle />
                    </span>
                  </OverlayTrigger>
                </span>

                <InputGroup>
                  <FusionInputField
                    type="number"
                    name="minEventTime"
                    onChange={setMinEventTime}
                    onBlur={() => setMinEventTime(Math.max(0, Math.min(7200, minEventTime)))}
                    value={minEventTime}
                    className="form-control border-0 bg-primary"
                  />
                  <InputGroup.Text className="text-dark">secs</InputGroup.Text>
                </InputGroup>
              </div>
            </div>

            {/* Cancel button */}
            <div className="d-flex justify-content-md-end">
              <Button
                variant="theme.secondary"
                className="fw-bold p-3"
                onClick={() => {
                  setShowNewGlobalAction(false);
                  setInitialData(null);
                }}
                style={{
                  background: theme.primary,
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>
      {/* end header card */}

      {/* builder */}
      <div className="flex-shrink-0">
        {list.map((item, index) => (
          <GlobalActionsDropdown
            key={index}
            itemNode={item}
            menuItems={menuItems[item.type]}
            index={index}
            setRetrigger={setRetrigger}
            submitHandler={submitHandler}
            anyErrors={anyErrors}
            submitting={submitting}
          />
        ))}

        {error && (
          <div style={{ color: 'red', margin: 10, textAlign: 'right' }}>{error.message}</div>
        )}
      </div>
    </div>
  );

  //TODO: Break up this giant function into smaller : Each switch can be it's own function
  //Convert the list of actions to a global action object
  function BuildGlobalAction(arr) {
    // console.log("Building global action", arr);
    let mode = 0;
    let obj = {
      state: 'inactive',
      title: actionTitle || 'New Global Action',
      debounceTime: debounceTime || 0,
      minEventTime: minEventTime || 0,
      actionName: '',
      triggerName: '',
      resolutionName: '',
      triggers: [],
      actions: [],
      resolutions: [],
      rawDataList: JSON.stringify(
        arr.map((item) => {
          const itemCopy = { ...item };
          delete itemCopy.next;
          delete itemCopy.prev;
          return itemCopy;
        }),
      ),
    };

    let trigger = [];
    let actionName = '';
    let triggerName = '';
    let resolutionName = '';
    let actionCount = 0;
    let triggerCount = 0;
    let resolutionCount = 0;

    let date;

    //create triggers
    arr.forEach((item, index) => {
      let objMap;

      if (item.type === 'if') {
        if (triggerCount === 0) triggerName = item.integrationType;
        triggerCount++;
      } else if (item.type === 'then') {
        if (actionCount === 0) actionName = item.integrationType;
        actionCount++;
      } else if (item.type === 'until') {
        if (resolutionCount === 0) resolutionName = item.integrationType;
        resolutionCount++;
      }

      //Start first switch
      //map object to expected values
      switch (item.integrationType) {
        //conditions
        case 'Device':
          objMap = {
            value: {
              type: 'device',
              connection: item.integrationData.deviceType,
              deviceId: item.integrationData.selectedDevice.id,
              nodeId: item.integrationData.selectedDevice.node_id,
              value1: {
                type: 'key',
                value: item.integrationData.keyToCheck,
              },
              operation: item.integrationData.operator,
              value2: {
                type: item.integrationData.inputValueType ? 'immediate' : 'key',
                value: item.integrationData.inputValueType
                  ? item.integrationData.valueToCompare
                  : item.integrationData.keyToCompare,
              },
            },
          };
          break;
        case 'Device Group':
          objMap = {
            value: {
              type: 'deviceGroup',
              connection: item.integrationData.deviceType,
              groupId: item.integrationData.group.id,
              value1: {
                type: 'key',
                value: item.integrationData.keyToCheck,
              },
              operation: item.integrationData.operator,
              value2: {
                type: item.integrationData.inputValueType ? 'immediate' : 'key',
                value: item.integrationData.inputValueType
                  ? item.integrationData.valueToCompare
                  : item.integrationData.keyToCompare,
              },
            },
          };
          break;
        case 'Date':
          {
            const [year, month, day] = item.integrationData.date.split('-').map(Number);
            date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));

            objMap = {
              value: {
                type: 'date',
                value: {
                  value: date,
                },
              },
            };
          }
          break;
        case 'Time':
          const strParts = item.integrationData.time.split(':');
          date = new Date();
          date.setHours(strParts[0], strParts[1], 0);

          objMap = {
            value: {
              type: 'time',
              value: {
                value: date,
              },
            },
          };
          break;
        case 'Date & Time':
          date = new Date(item.integrationData.dateTime);

          objMap = {
            value: {
              type: 'datetime',
              value: {
                value: date,
              },
            },
          };
          break;
        case 'Original Condition Resolved':
          objMap = {
            value: {
              type: 'triggerresolve',
              value: {},
            },
          };
          break;

        //actions
        case 'Create Fusion Alert':
          objMap = {
            value: {
              params: {
                function: 'createAlert',
                params: {
                  ...item.integrationData,
                },
              },
            },
          };
          break;
        case 'Toggle Manual Road Closure':
          objMap = {
            value: {
              params: {
                function: 'toggleFlashingLights',
                params: {
                  ...item.integrationData,
                },
              },
            },
          };
          break;
        case 'Device Action':
          objMap = {
            value: {
              function: 'deviceAction',
              params: {
                ...item.integrationData,
                deviceId: item.integrationData.selectedDevice.id,
                nodeId: item.integrationData.selectedDevice.node_id,
                actionType: item.integrationData.selectedFunction,
              },
            },
          };
          break;
        case 'Device Group Action':
          // console.log("Device Group Action", item.integrationData);
          objMap = {
            value: {
              function: 'deviceGroupAction',
              params: {
                ...item.integrationData,
                actionType: item.integrationData.selectedFunction,
              },
            },
          };
          break;
        case 'Send Email':
          objMap = {
            value: {
              function: 'sendEmail',
              params: {
                ...item.integrationData,
              },
            },
          };
          break;
        case 'Send SMS':
          objMap = {
            value: {
              function: 'sendSMS',
              params: {
                ...item.integrationData,
              },
            },
          };
          break;
        default:
          objMap = {
            value: {},
          };
          break;
      }
      //End first switch

      objMap = {
        ...objMap,
        option: index > 0 ? arr[index - 1].selectedAction : 'and',
      };

      //Start Second Switch
      switch (mode) {
        case 0: //if mode
          if (objMap.option.toLowerCase().includes('and')) trigger.push(objMap.value);
          else if (objMap.option.toLowerCase().includes('or')) {
            obj.triggers.push(trigger);
            trigger = [objMap.value];
          }

          if (objMap.option.toLowerCase().includes('then')) {
            obj.triggers.push(trigger);
            mode++;
            trigger = []; //reset trigger for until block
          } else break;
        case 1: //then mode
          if (
            (objMap.option.toLowerCase().includes('and') ||
              objMap.option.toLowerCase().includes('then')) &&
            !objMap.option.toLowerCase().includes('until') &&
            !objMap.option.toLowerCase().includes('done')
          ) {
            obj.actions.push(objMap.value);
            break;
          } else if (objMap.option.toLowerCase().includes('until')) {
            mode++;
          } else if (objMap.option.toLowerCase().includes('done')) {
            obj.resolutions.push([
              {
                type: 'triggerresolve',
                value: {},
              },
            ]);

            return;
          } else break;
        case 2: //until mode
          //   "Built global action: objMap.option",
          //   objMap.option,
          //   objMap
          // );

          if (!objMap.option.toLowerCase().includes('done')) {
            if (
              objMap.option.toLowerCase().includes('and') ||
              objMap.option.toLowerCase().includes('then') ||
              objMap.option.toLowerCase() === 'until'
            ) {
              trigger.push(objMap.value);
            } else if (objMap.option.toLowerCase().includes('or')) {
              obj.resolutions.push(trigger);
              trigger = [objMap.value];
            }

            break;
          } else {
            obj.resolutions.push(trigger);
            return;
          }
        default:
          break;
      }
    });
    //End Second Switch

    obj = {
      ...obj,
      actionName: actionCount === 1 ? actionName : 'Multiple',
      triggerName: triggerCount === 1 ? triggerName : 'Multiple',
      resolutionName:
        resolutionCount === 1 ? resolutionName : resolutionCount === 0 ? 'none' : 'Multiple',
    };

    return obj;
  }
};
